import {
  AlertCircle,
  Check,
  ChevronRight,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Navigate,
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useEffect, useRef, useState } from 'react';

import { parseTaggedText } from '@splitfiesta/core';
import {
  ACTUALLY_SUPPORTED_LANGUAGES,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import { Badge, Button, Card } from '@splitfiesta/ui';

import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import { Wordmark } from '@/components/common/Wordmark';
import { NativeAppInviteBlock } from '@/components/invitations/NativeAppInviteBlock';
import { PlaceholderClaimStep } from '@/components/invitations/PlaceholderClaimStep';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { useGroupQuery } from '@/hooks/queries/useGroups';
import {
  useAcceptInvitationMutation,
  useValidateInvitationQuery,
} from '@/hooks/queries/useInvitations';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { resolveUserName } from '@/lib/displayName';
import { nudgeStoreForPlatform, rememberDismissal } from '@/lib/nativeAppNudge';

import { useAuthStore } from '@/stores/authStore';

import { celebrate } from '@/utils/confetti';

/**
 * Invitation acceptance page
 * Handles:
 * - Token validation
 * - Group preview display
 * - Authentication flow
 * - Acceptance process
 */

// Tagged stand-ins translated in place of the two names: each locale keeps
// its own word order, and the names never pass through the tag parser.
const NAME_SLOT = '<name>_</name>';
const GROUP_SLOT = '<group>_</group>';

export default function InvitePage() {
  const params = useParams();
  const token = params.token;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('invitations');
  const [_hasAccepted, setHasAccepted] = useState(false);
  const [isAlreadyMember, setIsAlreadyMember] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [claimPlaceholderId, setClaimPlaceholderId] = useState<string | null>(
    null
  );
  // Ignores the 7-day nudge dismissal on purpose: the invite is the funnel,
  // so the app step leads on every invite a phone visitor opens.
  const [appStore] = useState(nudgeStoreForPlatform);
  const [webPathChosen, setWebPathChosen] = useState(false);
  const validationRef =
    useRef<ReturnType<typeof useValidateInvitationQuery>['data']>(undefined);
  const navigateRef = useRef(navigate);

  // Load additional namespaces
  useNamespace('common');
  useNamespace('errors');
  useNamespace('groups');

  // Apply language from URL param (shared by the inviter)
  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (
      langParam &&
      ACTUALLY_SUPPORTED_LANGUAGES.includes(
        langParam as (typeof ACTUALLY_SUPPORTED_LANGUAGES)[number]
      ) &&
      langParam !== i18n.language
    ) {
      i18n.changeLanguage(langParam);
    }
  }, [searchParams, i18n]);

  const {
    isAuthenticated,
    isLoading: authLoading,
    hasHydrated,
  } = useAuthStore();

  const { isOnline } = useNetworkStatus();

  const {
    data: validation,
    isLoading: isValidating,
    error: validationError,
  } = useValidateInvitationQuery(token ?? '', hasHydrated && !!token);

  // Get group details if invitation is valid
  const { data: group } = useGroupQuery(
    (validation as { groupId?: string })?.groupId || '',
    !!validation?.valid && !!(validation as { groupId?: string })?.groupId
  );

  // Accept invitation mutation
  const acceptInvitation = useAcceptInvitationMutation();

  // Update refs when values change
  useEffect(() => {
    validationRef.current = validation;
  }, [validation]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Check if user is already a member based on validation response
  useEffect(() => {
    if (validation?.isAlreadyMember && !isAlreadyMember && !isRedirecting) {
      // Use setTimeout to avoid setting state during render
      const timer = setTimeout(() => {
        setIsAlreadyMember(true);
        setIsRedirecting(true);
        setCountdown(10); // Start countdown from 10
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [validation?.isAlreadyMember, isAlreadyMember, isRedirecting]);

  // Handle countdown and redirect
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Redirect when countdown reaches 0
      const validationData = validationRef.current as {
        groupInfo?: { id?: string };
      };
      if (validationData?.groupInfo?.id) {
        navigateRef.current(`/groups/${validationData.groupInfo.id}`);
      } else {
        navigateRef.current('/groups');
      }
    }
  }, [countdown]); // Only depend on countdown, use refs for validation and navigate

  // Clean up any old pendingInvitation from sessionStorage if it exists
  useEffect(() => {
    // Remove any leftover pendingInvitation from previous implementations
    sessionStorage.removeItem('pendingInvitation');
  }, []);

  // Early return after all hooks have been called
  if (!token) {
    return <Navigate to="/groups" replace />;
  }

  // Handle acceptance
  const handleAccept = async () => {
    if (!token || acceptInvitation.isPending || _hasAccepted) return;

    try {
      const result = await acceptInvitation.mutateAsync({
        token,
        claimPlaceholderId: claimPlaceholderId ?? undefined,
      });
      setHasAccepted(true);

      // Trigger celebration confetti
      celebrate('high');

      // Wait a bit to show success state
      setTimeout(() => {
        if (result?.groupId) {
          navigate(`/groups/${result.groupId}`);
        } else {
          navigate('/groups');
        }
      }, 1500);
    } catch (error) {
      // Check if it's a 409 Conflict (already a member)
      const errorMessage = error instanceof Error ? error.message : '';
      const errorStatus = (error as { response?: { status?: number } })
        ?.response?.status;

      if (errorStatus === 409 || errorMessage.includes('already')) {
        setIsAlreadyMember(true);
        setIsRedirecting(true);
        setCountdown(10); // Start the 10-second countdown
        // The redirect will be handled by the countdown useEffect
      }
      // Other errors are handled by the mutation's onError callback
    }
  };

  const handleAuthRedirect = () => {
    // Pass the token and language as URL parameters to maintain the flow
    const langParam = searchParams.get('lang');
    const params = new URLSearchParams();
    if (token) params.set('invite', token);
    if (langParam) params.set('lang', langParam);
    navigate(`/login?${params.toString()}`);
  };

  // Loading state - wait for auth to hydrate before showing validation loading
  if (!hasHydrated || isValidating) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none p-4 pt-4">
        <div className="max-w-md w-full mx-auto space-y-4">
          {/* Theme Toggle and Language Switcher */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="invite-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          {/* Header */}
          <div className="text-center mb-4">
            <div className="loading-spinner-dark w-10 h-10 mx-auto mb-3"></div>
            <h1 className="flex justify-center">
              <Wordmark height={28} pulse />
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1 opacity-50">
              Loading invitation...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (validationError || !validation?.valid) {
    const errorKey =
      validationError instanceof Error
        ? validationError.message
        : 'invalidToken';

    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Theme Toggle and Language Switcher */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="invite-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          <Card className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('invalidTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t(errorKey)}
              </p>
              <RouterLink to="/" className="inline-block">
                <Button variant="primary">{t('common:navigation.home')}</Button>
              </RouterLink>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Already a member state
  if (isAlreadyMember) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Theme Toggle and Language Switcher */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="invite-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          <Card className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('alreadyMember')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('alreadyMemberMessage')}
              </p>
              {countdown !== null ? (
                <div className="flex flex-col items-center justify-center mt-4">
                  <div className="text-7xl font-bold text-gray-800 dark:text-gray-200 animate-pulse">
                    {countdown}
                  </div>
                  <p className="text-lg font-medium text-gray-600 dark:text-gray-400 mt-1">
                    {t(countdown === 1 ? 'second' : 'seconds')}
                  </p>
                </div>
              ) : (
                <Button variant="secondary" className="w-full mt-4">
                  {t('viewGroup')}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Success state (after acceptance)
  if (_hasAccepted) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Theme Toggle and Language Switcher */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="invite-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          <Card className="p-6 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('successTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('redirecting')}
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // A claim cannot be queued: react-query would pause the accept and replay
  // it later, by which time the placeholder may be gone or already claimed.
  // A plain join has no such coupling and stays available offline.
  const claimBlockedOffline = !isOnline && claimPlaceholderId !== null;

  const showAppStep =
    appStore !== null &&
    !webPathChosen &&
    !authLoading &&
    !validation?.isAlreadyMember;

  // Valid invitation view
  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none p-4 pt-4">
      <div className="max-w-md w-full mx-auto space-y-4">
        {/* Theme Toggle and Language Switcher */}
        <div className="flex justify-between items-center gap-3 mb-3">
          <ThemeToggle variant="auth" data-testid="invite-theme-toggle" />
          <LanguageSwitch variant="auth" />
        </div>

        {/* Header */}
        <div className="text-center mb-4">
          <RouterLink to={isAuthenticated ? '/groups' : '/'}>
            <h1 className="text-3xl font-bold text-white drop-shadow-lg hover:text-white/90 transition-colors cursor-pointer">
              {t('common:app.name')}
            </h1>
          </RouterLink>
          <p className="text-white/90 mt-1">
            {parseTaggedText(
              t('joinPromptWithInviter', {
                inviterName: NAME_SLOT,
                groupName: GROUP_SLOT,
              })
            ).map((segment, index) =>
              segment.tag ? (
                <strong key={index} className="font-bold text-white">
                  {segment.tag === 'name'
                    ? resolveUserName(validation?.invitedBy?.name, t)
                    : validation?.groupInfo?.name}
                </strong>
              ) : (
                segment.text
              )
            )}
          </p>
        </div>

        {/* Invitation Card */}
        <Card className="p-6">
          {/* Trust indicators */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-center">
              <Badge variant="success" className="text-xs">
                <Check className="w-3 h-3 mr-1" />
                {t('verifiedGroup')}
              </Badge>
            </div>
            {validation?.groupInfo?.memberCount && (
              <div className="text-center text-xs text-gray-600 dark:text-gray-300">
                {validation.groupInfo.memberCount === 1
                  ? t('activeMembers_one', {
                      count: validation.groupInfo.memberCount,
                    })
                  : t('activeMembers_other', {
                      count: validation.groupInfo.memberCount,
                    })}
              </div>
            )}
          </div>

          {/* Group Info */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-2xl mx-auto mb-3 flex items-center justify-center">
              <Users className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {validation?.groupInfo?.name || t('common:loading.text')}
            </h1>
            {group?.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {group.description}
              </p>
            )}
          </div>

          {!authLoading &&
            isAuthenticated &&
            !validation?.isAlreadyMember &&
            (validation?.placeholders?.length ?? 0) > 0 && (
              <PlaceholderClaimStep
                placeholders={validation?.placeholders ?? []}
                value={claimPlaceholderId}
                onChange={setClaimPlaceholderId}
                groupName={validation?.groupInfo?.name ?? ''}
                disabled={acceptInvitation.isPending}
              />
            )}

          {showAppStep && appStore && (
            <NativeAppInviteBlock
              store={appStore}
              onContinue={() => {
                rememberDismissal();
                setWebPathChosen(true);
              }}
            />
          )}

          {/* Action button - only show if user is NOT already a member */}
          {!authLoading && !validation?.isAlreadyMember && !showAppStep && (
            <div className="space-y-3">
              <Button
                onClick={
                  isAuthenticated
                    ? handleAccept
                    : validation?.requiresAuth
                      ? handleAuthRedirect
                      : handleAccept
                }
                variant="primary"
                className="w-full flex items-center justify-center"
                disabled={acceptInvitation.isPending || claimBlockedOffline}
                title={
                  claimBlockedOffline
                    ? t('groups:placeholderMembers.offlineDisabled')
                    : undefined
                }
              >
                <UserPlus className="w-4 h-4 mr-2" />
                <span className="font-medium text-white">
                  {acceptInvitation.isPending
                    ? t('common:loading.text')
                    : t('joinGroupButton', {
                        name: validation?.groupInfo?.name || 'this group',
                      })}
                </span>
              </Button>

              {/* Auth required message for unauthenticated users */}
              {!isAuthenticated && validation?.requiresAuth && (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                  {t('authRequired')}
                </p>
              )}
            </div>
          )}

          {/* This section will never be reached because isAlreadyMember triggers an early return */}
        </Card>

        {/* Skip link */}
        <div className="text-center">
          <RouterLink
            to="/groups"
            className="text-sm text-white/70 hover:text-white/90 transition-colors"
          >
            {t('continueWithout', {
              groupName: validation?.groupInfo?.name || 'this group',
            })}
            <ChevronRight className="w-3 h-3 inline ml-1" />
          </RouterLink>
        </div>
      </div>
    </div>
  );
}
